import React, { useContext, useEffect, useState } from "react";
import ContentContainer from "../components/ContentContainer.tsx";
import {
  Button,
  Checkbox,
  FormColumn2Wide,
  FormColumn3Wide,
  Input,
  Paper,
  Select,
  Typography,
  useAlerts,
  useLoading
} from "best-common-react";
import { useDropdowns } from "../contexts/DropdownsContext.tsx";
import { NewMarketingCampaign } from "../types/marketing-campaign.ts";
import SqlEditor from "../components/SqlEditor.tsx";
import { createMarketingCampaign, runQuery } from "../api/AuthenticatedClient.ts";
import { RunQueryResponse } from "../types/query.ts";
import styled, { ThemeContext } from "styled-components";
import { FACEBOOK_CUSTOM_AUDIENCE, LIVE_RAMP } from "../constants/marketingCampaign.ts";
import BullseyeSubSection from "../components/BullseyeSubSection.tsx";

type QueryResponseMessageProps = {
  color: string;
};

const QueryResponseMessage = styled.div<QueryResponseMessageProps>`
  color: ${props => props.color};
`;

const QueryRulesContainer = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

const RunQueryContainer = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const FooterButtonContainer = styled.div`
  display: flex;
  margin-top: 1rem;
  margin-bottom: 1rem;
  justify-content: flex-end;
`;

const defaultMarketingCampaign: NewMarketingCampaign = {
  campaignType: undefined,
  description: "",
  teamId: undefined,
  accountId: "",
  createAudienceFlag: false,
  audienceId: "",
  audienceName: "",
  querySql: ""
};

const CreateMarketingCampaign: React.FC = () => {
  const { setLoading } = useLoading();
  const { addAlert } = useAlerts();
  const Theme = useContext(ThemeContext);
  const { teamOptions, campaignTypeOptions } = useDropdowns();
  const [marketingCampaign, setMarketingCampaign] = useState<NewMarketingCampaign>(defaultMarketingCampaign);
  const [queryRunning, setQueryRunning] = useState(false);
  const [runQueryResponse, setRunQueryResponse] = useState<RunQueryResponse>();
  const [canSave, setCanSave] = useState(false);

  const handleChange = (key: keyof NewMarketingCampaign, value: unknown) => {
    setMarketingCampaign({ ...marketingCampaign, [key]: value });
  };

  const handleChanges = (patch: Partial<NewMarketingCampaign>) => {
    setMarketingCampaign({ ...marketingCampaign, ...patch });
  };

  const executeSqlQuery = async () => {
    try {
      setQueryRunning(true);
      const res = await runQuery(marketingCampaign.querySql);
      setRunQueryResponse(res);
    } catch (e) {
    } finally {
      setQueryRunning(false);
    }
  };

  const getQueryMessage = () => {
    // Query requires validation
    if (runQueryResponse?.isValid === undefined) {
      return <QueryResponseMessage color="inherit">Enter and Run query to validate.</QueryResponseMessage>;
    }

    // Invalid Query
    else if (!runQueryResponse?.isValid) {
      return (
        <QueryResponseMessage color={Theme?.colors["mlb-red"]}>{runQueryResponse.errorMessage}</QueryResponseMessage>
      );
    }

    // Valid Query
    else {
      return (
        <QueryResponseMessage color={Theme?.colors.green}>
          Total Customers: {runQueryResponse.totalRows}
        </QueryResponseMessage>
      );
    }
  };

  const save = async () => {
    try {
      setLoading(true);
      await createMarketingCampaign(marketingCampaign);
      setMarketingCampaign(defaultMarketingCampaign);
      setRunQueryResponse(undefined);
      addAlert({
        type: "success",
        text: "Successfully Saved Marketing Campaign"
      });
    } catch (e) {
      addAlert({
        type: "danger",
        text: "Error Saving Marketing Campaign"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Validate Details
    let validDetails =
      !!marketingCampaign.campaignType && !!marketingCampaign.teamId && !!marketingCampaign.description;
    if (marketingCampaign.campaignType == FACEBOOK_CUSTOM_AUDIENCE) {
      validDetails =
        validDetails &&
        !!marketingCampaign.accountId &&
        (marketingCampaign.createAudienceFlag ? !!marketingCampaign.audienceName : !!marketingCampaign.audienceId);
    } else if (marketingCampaign.campaignType == LIVE_RAMP) {
      validDetails = validDetails && !!marketingCampaign.audienceName;
    }

    const validQuerySql = !!runQueryResponse?.isValid;

    setCanSave(validDetails && validQuerySql);
  }, [marketingCampaign, runQueryResponse]);

  return (
    <ContentContainer>
      <Typography variant="h5">Create Marketing Campaign</Typography>
      <Paper>
        <BullseyeSubSection header="Details">
          <div className="row align-items-center">
            <FormColumn3Wide>
              <Select
                id="destination-select"
                label="Destination"
                value={campaignTypeOptions.find(option => option.value === marketingCampaign?.campaignType)}
                options={campaignTypeOptions}
                onChange={option => {
                  if (option?.value == LIVE_RAMP) {
                    handleChanges({
                      campaignType: option?.value,
                      createAudienceFlag: false,
                      accountId: undefined,
                      audienceId: undefined
                    });
                  } else {
                    handleChange("campaignType", option?.value);
                  }
                }}
              />
            </FormColumn3Wide>
            <FormColumn3Wide>
              <Select
                id="club-select"
                label="Club"
                value={teamOptions.find(option => option.value.id === marketingCampaign?.teamId)}
                options={teamOptions}
                onChange={option => {
                  handleChange("teamId", option?.value.id);
                }}
              />
            </FormColumn3Wide>
            {marketingCampaign.campaignType == FACEBOOK_CUSTOM_AUDIENCE && (
              <FormColumn3Wide>
                <Checkbox
                  id="new-audience-checkbox"
                  label="Create new audience?"
                  checked={!!marketingCampaign?.createAudienceFlag}
                  onChange={checked => {
                    handleChanges({
                      createAudienceFlag: checked,
                      audienceId: "",
                      audienceName: ""
                    });
                  }}
                />
              </FormColumn3Wide>
            )}
          </div>

          <div className="row mt-3">
            <Input
              id="description-input"
              label="Description"
              value={marketingCampaign.description}
              onChange={value => {
                handleChange("description", value);
              }}
            />
          </div>

          <div className="row mt-3">
            {marketingCampaign.campaignType == FACEBOOK_CUSTOM_AUDIENCE && (
              <FormColumn2Wide>
                <Input
                  id="account-id-input"
                  label="AD Account ID"
                  value={marketingCampaign?.accountId}
                  onChange={value => {
                    handleChange("accountId", value);
                  }}
                />
              </FormColumn2Wide>
            )}
            <FormColumn2Wide>
              {(marketingCampaign.campaignType == LIVE_RAMP ||
                (marketingCampaign.campaignType == FACEBOOK_CUSTOM_AUDIENCE &&
                  !!marketingCampaign?.createAudienceFlag)) && (
                <Input
                  id="audience-name-input"
                  label="Audience Name"
                  value={marketingCampaign?.audienceName}
                  onChange={value => {
                    handleChange("audienceName", value);
                  }}
                />
              )}
              {marketingCampaign.campaignType == FACEBOOK_CUSTOM_AUDIENCE && !marketingCampaign?.createAudienceFlag && (
                <Input
                  id="audience-id-input"
                  label="Audience ID"
                  value={marketingCampaign?.audienceId}
                  onChange={value => {
                    handleChange("audienceId", value);
                  }}
                />
              )}
            </FormColumn2Wide>
          </div>
        </BullseyeSubSection>

        <BullseyeSubSection header="SQL Query">
          <QueryRulesContainer>
            <div>Query Rules:</div>
            <ol>
              <li>customer_id must be selected in the query</li>
              <li>customer_id must be a list of INT64 email/customer ID's</li>
              <li>Do not include any comments in the query (i.e. "-comment")</li>
              <li>Do not include a semicolon at the end of the query</li>
            </ol>
          </QueryRulesContainer>

          <RunQueryContainer>
            <Button onClick={executeSqlQuery} loading={queryRunning} loadingType="spinner">
              <i className="fas fa-play me-2" />
              Run
            </Button>
            {getQueryMessage()}
          </RunQueryContainer>
          <SqlEditor
            value={marketingCampaign?.querySql}
            onChange={value => {
              setRunQueryResponse(undefined);
              handleChange("querySql", value);
            }}
            disabled={queryRunning}
          />
        </BullseyeSubSection>
        <FooterButtonContainer>
          <Button variant="primary" onClick={save} disabled={!canSave}>
            Save
          </Button>
        </FooterButtonContainer>
      </Paper>
    </ContentContainer>
  );
};

export default CreateMarketingCampaign;
